import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../context/LanguageContext';
import { 
  Sun, 
  Home, 
  Zap, 
  Battery, 
  TrendingUp,
  Cloud,
  CloudSun,
  Sunrise,
  Sunset,
  Moon,
  Euro,
  Leaf
} from 'lucide-react';

const dashTranslations = {
  de: {
    titlePre: 'Energie ', titleHighlight: 'Dashboard',
    subtitle: 'Sehen Sie in Echtzeit, wie Ihre Solaranlage arbeitet und Geld spart.',
    currentTime: 'Aktuelle Zeit', weather: 'Wetter', savedToday: 'Heute gespart',
    solarProduction: 'Solarproduktion', active: '🔆 Aktiv', night: '🌙 Nacht',
    houseConsumption: 'Hausverbrauch', selfSufficient: '✓ Autark', gridDraw: '⚡ Netzbezug',
    storage: 'Speicher', charging: '⚡ Lädt', discharging: '🔋 Entlädt', low: '⚠️ Niedrig',
    selfConsumption: 'Eigenverbrauch', gridFeed: 'Netzeinspeisung', gridImport: 'Netzbezug',
    co2SavedToday: 'CO₂ gespart heute',
    disclaimer: '* Simulation basierend auf einem 8.5 kWp System mit 10 kWh Speicher. Tatsächliche Werte können abweichen.',
    locale: 'de-DE'
  },
  en: {
    titlePre: 'Energy ', titleHighlight: 'Dashboard',
    subtitle: 'See in real time how your solar system works and saves money.',
    currentTime: 'Current Time', weather: 'Weather', savedToday: 'Saved Today',
    solarProduction: 'Solar Production', active: '🔆 Active', night: '🌙 Night',
    houseConsumption: 'Home Consumption', selfSufficient: '✓ Self-sufficient', gridDraw: '⚡ Grid Draw',
    storage: 'Storage', charging: '⚡ Charging', discharging: '🔋 Discharging', low: '⚠️ Low',
    selfConsumption: 'Self-Consumption', gridFeed: 'Grid Feed-in', gridImport: 'Grid Draw',
    co2SavedToday: 'CO₂ saved today',
    disclaimer: '* Simulation based on an 8.5 kWp system with 10 kWh storage. Actual values may vary.',
    locale: 'en-US'
  }
};

const LiveEnergyDashboard = () => {
  const { language } = useLanguage();
  const t = dashTranslations[language] || dashTranslations.de;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [production, setProduction] = useState(0);
  const [consumption, setConsumption] = useState(0);
  const [batteryLevel, setBatteryLevel] = useState(65);
  const [weather, setWeather] = useState('sunny');
  const [animatedSavings, setAnimatedSavings] = useState(0);

  // Single timer drives time + simulated energy values (state updates happen
  // inside the interval callback, not synchronously in the effect body)
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      setCurrentTime(now);

      const timeDecimal = now.getHours() + now.getMinutes() / 60;

      // Solar production curve (peaks at noon)
      let baseProduction = 0;
      if (timeDecimal >= 6 && timeDecimal <= 20) {
        const x = (timeDecimal - 13) / 4;
        baseProduction = 8.5 * Math.exp(-x * x) * 1000; // Max 8.5kW
      }
      const weatherFactors = { sunny: 1, partlyCloudy: 0.75, cloudy: 0.4 };
      baseProduction *= weatherFactors[weather] || 1;
      baseProduction += (Math.random() - 0.5) * 200;
      baseProduction = Math.max(0, Math.round(baseProduction));

      // Simulate consumption (higher in morning and evening)
      let baseConsumption = 800;
      if (timeDecimal >= 6 && timeDecimal <= 9) baseConsumption = 2000;
      if (timeDecimal >= 17 && timeDecimal <= 22) baseConsumption = 2500;
      baseConsumption = Math.round(baseConsumption + (Math.random() - 0.5) * 300);

      setProduction(baseProduction);
      setConsumption(baseConsumption);

      const netEnergy = baseProduction - baseConsumption;
      setBatteryLevel(prev => Math.min(100, Math.max(10, prev + (netEnergy > 0 ? 0.1 : -0.05))));
      setAnimatedSavings(prev => prev + ((baseProduction / 1000) * 0.35) / 3600);
    };

    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [weather]);

  const getTimeIcon = () => {
    const hour = currentTime.getHours();
    if (hour >= 6 && hour < 10) return <Sunrise className="w-6 h-6 text-orange-400" />;
    if (hour >= 10 && hour < 17) return <Sun className="w-6 h-6 text-yellow-400" />;
    if (hour >= 17 && hour < 20) return <Sunset className="w-6 h-6 text-orange-500" />;
    return <Moon className="w-6 h-6 text-blue-300" />;
  };

  const getWeatherIcon = () => {
    switch (weather) {
      case 'sunny': return <Sun className="w-8 h-8 text-yellow-400" />;
      case 'partlyCloudy': return <CloudSun className="w-8 h-8 text-gray-300" />;
      case 'cloudy': return <Cloud className="w-8 h-8 text-gray-400" />;
      default: return <Sun className="w-8 h-8 text-yellow-400" />;
    }
  };

  const selfConsumptionRate = production > 0 
    ? Math.min(100, Math.round((Math.min(production, consumption) / production) * 100))
    : 0;

  const gridExport = Math.max(0, production - consumption);
  const gridImport = Math.max(0, consumption - production);

  return (
    <section id="live-dashboard" className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-green-500/20 text-green-400 px-4 py-2 rounded-full text-sm font-medium mb-4 animate-pulse">
            <Zap className="w-4 h-4" />
            LIVE
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            {t.titlePre}<span className="text-green-400">{t.titleHighlight}</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* Time and Weather Bar */}
        <div className="flex flex-wrap justify-center gap-4 mb-8">
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4 flex items-center gap-3">
              {getTimeIcon()}
              <div>
                <p className="text-gray-400 text-xs">{t.currentTime}</p>
                <p className="text-white font-bold text-lg">
                  {currentTime.toLocaleTimeString(t.locale, { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4 flex items-center gap-3">
              {getWeatherIcon()}
              <div>
                <p className="text-gray-400 text-xs">{t.weather}</p>
                <div className="flex gap-2">
                  {['sunny', 'partlyCloudy', 'cloudy'].map(w => (
                    <button
                      key={w}
                      onClick={() => setWeather(w)}
                      className={`px-2 py-1 rounded text-xs transition-colors ${
                        weather === w ? 'bg-amber-500 text-black' : 'bg-white/10 text-gray-300'
                      }`}
                    >
                      {w === 'sunny' ? '☀️' : w === 'partlyCloudy' ? '⛅' : '☁️'}
                    </button>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-r from-green-600/30 to-emerald-600/30 border-green-500/30">
            <CardContent className="p-4 flex items-center gap-3">
              <Euro className="w-6 h-6 text-green-400" />
              <div>
                <p className="text-gray-400 text-xs">{t.savedToday}</p>
                <p className="text-green-400 font-bold text-lg">
                  {animatedSavings.toFixed(2)} €
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Dashboard */}
        <div className="relative max-w-5xl mx-auto">
          {/* Energy Flow Visualization */}
          <div className="grid grid-cols-3 gap-4 md:gap-8 items-center">
            
            {/* Solar Panel */}
            <Card className="bg-gradient-to-br from-amber-900/50 to-orange-900/50 border-amber-600/30">
              <CardContent className="p-6 text-center">
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <div className={`absolute inset-0 bg-amber-400/30 rounded-full ${production > 0 ? 'animate-ping' : ''}`}></div>
                  <div className="relative w-20 h-20 bg-gradient-to-br from-amber-400 to-orange-500 rounded-lg flex items-center justify-center shadow-lg shadow-amber-500/30">
                    <Sun className={`w-10 h-10 text-white ${production > 0 ? 'animate-spin-slow' : ''}`} />
                  </div>
                </div>
                <h3 className="text-white font-bold mb-1">{t.solarProduction}</h3>
                <p className="text-3xl font-bold text-amber-400">
                  {(production / 1000).toFixed(2)} <span className="text-lg">kW</span>
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {production > 0 ? t.active : t.night}
                </p>
              </CardContent>
            </Card>

            {/* House / Consumption */}
            <Card className="bg-gradient-to-br from-sky-900/50 to-blue-900/50 border-sky-600/30 transform scale-110 z-10">
              <CardContent className="p-6 text-center">
                <div className="relative w-24 h-24 mx-auto mb-4">
                  {/* Animated energy flow */}
                  {production > consumption && (
                    <div className="absolute -left-8 top-1/2 w-8 h-1 overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-r from-amber-400 to-transparent animate-flow-right"></div>
                    </div>
                  )}
                  {production < consumption && (
                    <div className="absolute -left-8 top-1/2 w-8 h-1 overflow-hidden">
                      <div className="w-full h-full bg-gradient-to-l from-red-400 to-transparent animate-flow-left"></div>
                    </div>
                  )}
                  <div className="w-24 h-24 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-sky-500/30">
                    <Home className="w-12 h-12 text-white" />
                  </div>
                </div>
                <h3 className="text-white font-bold mb-1">{t.houseConsumption}</h3>
                <p className="text-3xl font-bold text-sky-400">
                  {(consumption / 1000).toFixed(2)} <span className="text-lg">kW</span>
                </p>
                <div className="mt-2 flex justify-center gap-2">
                  <span className={`px-2 py-1 rounded text-xs ${
                    production >= consumption 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {production >= consumption ? t.selfSufficient : t.gridDraw}
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Battery */}
            <Card className="bg-gradient-to-br from-emerald-900/50 to-green-900/50 border-emerald-600/30">
              <CardContent className="p-6 text-center">
                <div className="relative w-20 h-20 mx-auto mb-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/30">
                    <Battery className="w-10 h-10 text-white" />
                  </div>
                  {/* Battery level indicator */}
                  <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-16 h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-1000 ${
                        batteryLevel > 50 ? 'bg-green-400' : batteryLevel > 20 ? 'bg-amber-400' : 'bg-red-400'
                      }`}
                      style={{ width: `${batteryLevel}%` }}
                    ></div>
                  </div>
                </div>
                <h3 className="text-white font-bold mb-1">{t.storage}</h3>
                <p className="text-3xl font-bold text-emerald-400">
                  {batteryLevel.toFixed(0)} <span className="text-lg">%</span>
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  {production > consumption ? t.charging : batteryLevel > 20 ? t.discharging : t.low}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <TrendingUp className="w-6 h-6 text-green-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">{t.selfConsumption}</p>
                <p className="text-white font-bold text-xl">{selfConsumptionRate}%</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <Zap className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">{t.gridFeed}</p>
                <p className="text-white font-bold text-xl">{(gridExport / 1000).toFixed(2)} kW</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <Zap className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">{t.gridImport}</p>
                <p className="text-white font-bold text-xl">{(gridImport / 1000).toFixed(2)} kW</p>
              </CardContent>
            </Card>
            
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-4 text-center">
                <Leaf className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <p className="text-gray-400 text-xs">{t.co2SavedToday}</p>
                <p className="text-white font-bold text-xl">{((production / 1000) * 0.4).toFixed(1)} kg</p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Info Text */}
        <p className="text-center text-gray-500 text-sm mt-8">
          {t.disclaimer}
        </p>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes flow-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        @keyframes flow-left {
          0% { transform: translateX(100%); }
          100% { transform: translateX(-100%); }
        }
        .animate-flow-right {
          animation: flow-right 1s linear infinite;
        }
        .animate-flow-left {
          animation: flow-left 1s linear infinite;
        }
        .animate-spin-slow {
          animation: spin 10s linear infinite;
        }
      ` }} />
    </section>
  );
};

export default LiveEnergyDashboard;
